import { FormEvent, useEffect, useRef, useState } from 'react';
import {
  getTermsAcceptance, getTermsDocuments, recordTermsAcceptance, sha256Hex,
  type LegalDocumentText, type TermsAcceptanceState,
} from '../api/legal';
import { ApiClientError } from '../api/http';
import { LegalText } from './LegalText';

const DOCUMENT_LABELS: Record<string, string> = {
  terms: 'Terms of Service', privacy: 'Privacy Policy', 'acceptable-use': 'Acceptable Use Policy',
};

export function documentLabel(id: string): string { return DOCUMENT_LABELS[id] || id; }

/**
 * All the documents in ONE scrollable box with headings (F-6): the person reads them without leaving
 * the page. A small "open in a new tab" link per document stays for anyone who wants to print.
 */
export function LegalDocumentsBox({ documents, label }: { documents: LegalDocumentText[]; label: string }) {
  const boxRef = useRef<HTMLDivElement>(null);
  function jump(id: string) {
    const box = boxRef.current;
    const target = box?.querySelector<HTMLElement>(`[data-legal-doc="${id}"]`);
    if (box && target) box.scrollTop = target.offsetTop - box.offsetTop;
  }
  return (
    <div className="legal-documents">
      <p className="legal-jump">Jump to:{' '}
        {documents.map((doc, index) => <span key={doc.id}>{index ? ' · ' : ''}
          <button type="button" className="link-button" onClick={() => jump(doc.id)}>{doc.title}</button>
        </span>)}
      </p>
      <div className="legal-scroll-box" ref={boxRef} tabIndex={0} role="region" aria-label={label}>
        {documents.map((doc) => (
          <section key={doc.id} data-legal-doc={doc.id} aria-labelledby={`legal-doc-${doc.id}`}>
            <h3 id={`legal-doc-${doc.id}`}>{doc.title}</h3>
            <LegalText source={doc.text} idPrefix={doc.id} />
          </section>
        ))}
      </div>
      <p className="settings-account-note">Prefer a separate page to read or print?{' '}
        {documents.map((doc, index) => <span key={doc.id}>{index ? ' · ' : ''}
          <a href={doc.url} target="_blank" rel="noopener noreferrer">{doc.title} (new tab)</a>
        </span>)}
      </p>
    </div>
  );
}

/** What the screen rendered: the signed documents' text in the server's order, and the digest of each. */
interface ShownDocuments { documents: LegalDocumentText[]; digests: Record<string, string>; }

/**
 * Hashes the text about to be rendered and checks it against the digests the acceptance state lists.
 * `null` when the in-app text is unavailable; throws when it is available but is not the current text.
 */
async function documentsToShow(state: TermsAcceptanceState,
  loaded: PromiseSettledResult<{ currentVersion: string; documents: LegalDocumentText[] }>): Promise<ShownDocuments | null> {
  if (loaded.status !== 'fulfilled') return null;
  const signed = loaded.value.documents.filter((doc) => doc.signed);
  const digests: Record<string, string> = {};
  for (const doc of signed) digests[doc.id] = await sha256Hex(doc.text);
  // The digests are the binding: the rendered text must be exactly what the acceptance state pins.
  const matches = signed.length === state.documents.length
    && state.documents.every((doc) => Boolean(digests[doc.id]) && (!doc.sha256 || doc.sha256 === digests[doc.id]));
  if (!matches) throw new Error('documents_not_current');
  // Shown in the order the acceptance state lists them.
  return { documents: state.documents.map((doc) => signed.find((item) => item.id === doc.id)!), digests };
}

/**
 * `onboarding` (default): the Welcome step, which may be skipped (the server gate still applies).
 * `gate`: the blocking screen shown after sign-in and whenever the terms version changes. No skip
 * button; once the server confirms acceptance `onContinue` is called with the confirmed state.
 */
export function TermsAcceptancePanel({ onContinue, mode = 'onboarding' }: {
  onContinue: (state?: TermsAcceptanceState) => void; mode?: 'onboarding' | 'gate';
}) {
  const gate = mode === 'gate';
  const [state, setState] = useState<TermsAcceptanceState | null>(null);
  const [shown, setShown] = useState<ShownDocuments | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [agreed, setAgreed] = useState(false);
  const [adult, setAdult] = useState(false);
  const [error, setError] = useState('');
  const live = useRef(false);
  const saving = useRef(false);

  async function load() {
    setLoading(true);
    setError('');
    setState(null);
    setShown(null);
    setAgreed(false);
    setAdult(false);
    try {
      const [current, texts] = await Promise.all([
        getTermsAcceptance(), Promise.allSettled([getTermsDocuments()]).then(([only]) => only),
      ]);
      let documents: ShownDocuments | null = null;
      try {
        documents = await documentsToShow(current, texts);
      } catch {
        if (live.current) setError('The documents changed while this page was loading. Reload the current terms to read them.');
        return;
      }
      if (live.current) { setState(current); setShown(documents); }
    } catch {
      if (live.current) setError('Terms and acceptance status could not be loaded. Please try again.');
    } finally {
      if (live.current) setLoading(false);
    }
  }

  useEffect(() => {
    live.current = true;
    void load();
    return () => { live.current = false; };
  }, []);

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (!state || !shown || state.accepted || !agreed || !adult || saving.current) return;
    saving.current = true;
    setBusy(true);
    setError('');
    try {
      // The digests of the text this screen rendered, never digests copied from the server's list.
      const confirmed = await recordTermsAcceptance(state, shown.digests);
      if (!live.current) return;
      if (confirmed.accepted) setState(confirmed);
      setAgreed(false);
      setAdult(false);
      if (!confirmed.accepted) {
        // The current documents may have changed: show (and hash) the new text before asking again.
        await load();
        if (live.current) setError('The current terms have changed or acceptance is not confirmed. Review the current documents and accept again.');
      }
      else if (gate) onContinue(confirmed);
    } catch (failure) {
      if (!live.current) return;
      if (failure instanceof ApiClientError && failure.code === 'terms_text_changed') {
        // The documents changed while this screen was open: show the current ones and ask again.
        await load();
        if (live.current) setError('The documents changed while this page was open. Review the current documents and accept again.');
      } else {
        setError('Acceptance could not be confirmed. Retry or reload the current terms; we have not marked them accepted here.');
      }
    } finally {
      saving.current = false;
      if (live.current) setBusy(false);
    }
  }

  return (
    <section className={gate ? 'onboarding-step terms-gate' : 'onboarding-step'} aria-labelledby="onboarding-terms-title">
      <h2 id="onboarding-terms-title">
        {gate && state?.acceptedVersion && !state.accepted ? 'Our terms have been updated' : 'Terms and policies'}
      </h2>
      {gate ? <p>Before you create a workspace or run paid generation, please review and accept the current
        Terms of Service, Privacy Policy and Acceptable Use Policy.</p> : null}
      {loading ? <p role="status">Loading current terms…</p> : null}
      {state ? <>
        <p>Current version: {state.currentVersion}</p>
        {gate && state.acceptedVersion && !state.accepted
          ? <p>You accepted version {state.acceptedVersion}. Version {state.currentVersion} needs your acceptance.</p>
          : null}
        {shown ? <LegalDocumentsBox documents={shown.documents}
          label="Terms of Service, Privacy Policy and Acceptable Use Policy" /> : <>
          <p>The documents could not be shown here right now. You can read them on our website (each opens in a
            new tab), but acceptance is recorded only for text shown on this screen: reload the current terms to
            continue.</p>
          <ul>{state.documents.map((doc) => <li key={doc.id}>
            <a href={doc.url} target="_blank" rel="noopener noreferrer">{documentLabel(doc.id)}</a>
          </li>)}</ul>
        </>}
        {state.accepted ? <p role="status">Acceptance recorded for version {state.currentVersion}.</p> : !shown ? null : <form onSubmit={submit}>
          <label><input type="checkbox" checked={agreed} disabled={busy}
            onChange={(event) => setAgreed(event.target.checked)} /> I have read and accept the Terms of Service,
            Privacy Policy and Acceptable Use Policy shown above.</label>
          <label><input type="checkbox" checked={adult} disabled={busy}
            onChange={(event) => setAdult(event.target.checked)} /> I confirm that I am an adult.</label>
          <button type="submit" disabled={busy || !agreed || !adult}>
            {busy ? 'Recording acceptance…' : gate ? 'Accept' : 'Record acceptance'}
          </button>
        </form>}
      </> : null}
      {error ? <p role="alert">{error}</p> : null}
      {!loading && !busy ? <button type="button" className="secondary" onClick={() => void load()}>Reload current terms</button> : null}
      {gate && state?.accepted ? <button type="button" onClick={() => onContinue(state)}>Continue</button> : null}
      {gate ? null : <>
        <button type="button" disabled={busy} onClick={() => onContinue(state?.accepted ? state : undefined)}>
          {state?.accepted ? 'Continue' : 'Continue without recording acceptance'}
        </button>
        {!state?.accepted ? <p>Continuing does not record acceptance. You can return to Welcome to review these documents later.
          Creating a workspace or running paid generation needs acceptance.</p> : null}
      </>}
    </section>
  );
}